import { useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker, Select } from 'antd';
import { PeriodTabs } from '../PeriodTabs';
import { donemAraligiMetni, SATIS_DONEM_SECENEKLERI, type Donem } from './donem';
import { KanalSecici } from './KanalSecici';
import type { useSatisAnalizFiltre } from './useSatisAnalizFiltre';

const { RangePicker } = DatePicker;

interface Props {
  filtre: ReturnType<typeof useSatisAnalizFiltre>;
  subeler: string[];
  /** Sayfada dogrulanmis sube (listede yoksa null). */
  sube: string | null;
  kanal: string | null;
  donem: Donem;
}

/**
 * G2b — Satis Analiz filtre seridi: donem sekmeleri, sube, kanal, aralik. Sayfa bunu header'in hemen altindaki
 * filtre barina (FiltreBarPortal) basar; yuvasiz kabukta sayfa icindeki yapisik seride. Olculer Yerlice
 * `FiltreBar`'i gibi kucuk (kullanici 2026-09-15): kompakt sekmeler, `size="small"` seciciler.
 */
export function FiltreSeridi({ filtre, subeler, sube, kanal, donem }: Props) {
  const ozelDeger = useMemo<[Dayjs, Dayjs] | null>(
    () => (filtre.ozelAralik ? [dayjs(filtre.ozelAralik.bas), dayjs(filtre.ozelAralik.bit)] : null),
    [filtre.ozelAralik],
  );

  /**
   * Ozel'de secili aralik RangePicker'in IKI input'unda zaten yazar; ayni tarihleri yaninda ikinci kez
   * basmak seridi 1280px'te iki satira dusuruyordu (kullanici 2026-09-16). Aralik SECILI DEGILSE picker
   * bostur ama ekran Aylik pencereyi gosterir (useSatisAnalizFiltre `hesapTuru`) — metin o zaman TEK
   * gostergedir, kalir.
   */
  const aralikPickerdaYaziyor = filtre.donemTuru === 'ozel' && filtre.ozelAralik !== null;

  return (
    <div data-testid="filtre-bari" className="min-w-0">
      {/* 🔴 390px'te olculdu (2026-08-21): serit sarmazsa `main` onu KESER, seciciler erisilemez. */}
      {/* 🔴 min-h-[28px] (2026-08-29 olculdu, 2026-09-16'da span'den BURAYA tasindi): sube secici sorgu
          donunce belirir; satir onun olculmus yuksekligini KOSULSUZ ayirir, yoksa dar ekranda +11px kayma.
          Aralik metni Ozel'de dustugu icin guard artik satirin kendisinde. */}
      <div className="flex flex-wrap items-center gap-2 min-h-[28px]" data-testid="donem-secici">
        <PeriodTabs
          value={filtre.donemTuru}
          onChange={(p) => filtre.degistir({ donemTuru: p })}
          secenekler={SATIS_DONEM_SECENEKLERI}
          kompakt
        />
        {/* Sube suzgeci: yalniz BIRDEN FAZLA sube varsa anlamli. */}
        {subeler.length > 1 && (
          <div data-testid="sube-secici">
            <Select
              size="small"
              style={{ minWidth: 140 }}
              value={sube ?? '__tum__'}
              onChange={(v) => filtre.degistir({ sube: v === '__tum__' ? null : v })}
              options={[
                { value: '__tum__', label: 'Tüm şubeler' },
                ...subeler.map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>
        )}
        <KanalSecici value={kanal} onChange={(k) => filtre.degistir({ kanal: k })} />
        {!aralikPickerdaYaziyor && (
          <span
            className="inline-flex items-center min-h-[28px] text-[12px] text-ink-500 tnum"
            data-testid="donem-araligi"
          >
            {donemAraligiMetni(donem)}
          </span>
        )}
        {filtre.donemTuru === 'ozel' && (
          // RangePicker testid'yi iki input'a yayar; testid sarmalayicida.
          <div data-testid="ozel-aralik">
            <RangePicker
              value={ozelDeger}
              onChange={(v) =>
                filtre.degistir({
                  ozelAralik:
                    v && v[0] && v[1]
                      ? { bas: v[0].format('YYYY-MM-DD'), bit: v[1].format('YYYY-MM-DD') }
                      : null,
                })
              }
              format="DD.MM.YYYY"
              size="small"
              allowClear
            />
          </div>
        )}
      </div>
    </div>
  );
}
